import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { RECOVERED_CLIPPED_ATTR, RECOVERED_CLIPPED_VALUE } from '@avisoma/shared/dist/htmlRecoveredMarker.js';
import backend from '../../services/backend';
import { Button } from '../../catalyst/button';
import { Dialog, DialogBody, DialogTitle } from '../../catalyst/dialog';
import { Switch } from '../../catalyst/switch';

// `!important` needed to beat the recovered spans' own inline `style="display:none"`.
// The attribute/value are shared with the converter that emits the spans
// (`server/src/lib/PDFToHTML/recoverClipped.ts`) so this selector can't drift.
const RECOVERED_STYLE = `<style>
span[${RECOVERED_CLIPPED_ATTR}="${RECOVERED_CLIPPED_VALUE}"] {
  display: inline !important;
  background-color: #fef08a;
  color: #713f12;
  outline: 1px solid #facc15;
  border-radius: 2px;
  padding: 0 1px;
}
</style>`;

// Inject the recovered-text stylesheet just inside <head> (or after <html> if
// there's no <head>) so the iframe stays in standards mode. Prepending before
// the leading `<!DOCTYPE html>` would flip it into quirks mode; the converter
// prompt guarantees the doctype, but the fallback prepend keeps the toggle
// working even if a conversion ever omits both <head> and <html>.
function injectRecoveredStyle(html: string): string {
  if (/<head[^>]*>/i.test(html)) return html.replace(/<head[^>]*>/i, (tag) => tag + RECOVERED_STYLE);
  if (/<html[^>]*>/i.test(html)) return html.replace(/<html[^>]*>/i, (tag) => tag + RECOVERED_STYLE);
  return RECOVERED_STYLE + html;
}

// Untrusted model-generated markup from a customer PDF, so it's rendered via a fully-sandboxed iframe, never `dangerouslySetInnerHTML`.
export function HtmlConversionDialog({
  pageId,
  token,
  open,
  onClose,
}: {
  pageId: string;
  token: string;
  open: boolean;
  onClose: () => void;
}) {
  const [showRecovered, setShowRecovered] = useState(false);

  const { data, isLoading, isError } = useQuery({
    // Keyed on token, not just pageId — a re-extraction stamps a new token, and
    // the cached response for the old one must not be served for the new one.
    queryKey: ['documentPageHtml', pageId, token],
    queryFn: () => backend.getDocumentPageHtml(pageId).then((res) => res.data),
    enabled: open,
    // Content is immutable per page + conversion token — nothing invalidates it.
    staleTime: 5 * 60 * 1000,
  });

  const srcDoc = useMemo(() => {
    if (!data) return '';
    return showRecovered ? injectRecoveredStyle(data.html) : data.html;
  }, [data, showRecovered]);

  return (
    <Dialog open={open} onClose={onClose} size="5xl">
      <DialogTitle className="flex items-center justify-between gap-3">
        <span className="flex items-baseline gap-2">
          Extracted HTML
          {data && (
            <span className="text-xs font-normal text-zinc-500 dark:text-zinc-400">
              {data.verdict} · converted {new Date(data.convertedAt).toLocaleString()}
            </span>
          )}
        </span>
        <Button outline onClick={onClose} aria-label="Close">
          <XMarkIcon className="size-4" />
        </Button>
      </DialogTitle>
      <DialogBody>
        {isLoading ? (
          <p className="text-sm text-gray-500 dark:text-zinc-400">Loading HTML…</p>
        ) : isError || data == null ? (
          <p className="text-sm text-red-600 dark:text-red-400">Failed to load HTML.</p>
        ) : (
          <div className="space-y-3">
            {data.verdict === 'DEGRADED' && (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/10 dark:text-amber-300">
                This conversion is <span className="font-medium">DEGRADED</span> — verification found defects
                against the PDF text layer. Default extract uses this HTML except on trusted-only
                categories.
              </p>
            )}
            <label className="flex items-center gap-2 text-sm text-gray-500 dark:text-zinc-400">
              <Switch
                checked={showRecovered}
                onChange={setShowRecovered}
                aria-label="Show recovered clipped text"
              />
              Show recovered clipped text
            </label>
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-zinc-700">
              <iframe
                title="Extracted HTML"
                sandbox=""
                srcDoc={srcDoc}
                className="h-[70vh] w-full bg-white"
              />
            </div>
          </div>
        )}
      </DialogBody>
    </Dialog>
  );
}
